import type { ReactNode } from "react";
import { Button, Grid } from "@smartfaster-ui/components";
import { optionButtonStyle } from "./OptionGroup.css";

export type OptionItem = {
  id: string;
  label: ReactNode;
  /**
   * Optional content shown before the label (e.g. a color swatch).
   */
  leading?: ReactNode;
};

type OptionGroupProps = {
  options: OptionItem[];
  value: string | null;
  /**
   * Prevents interaction with all non-selected options.
   */
  disabled?: boolean;
  /**
   * When false, clicking the selected option does nothing (radio behavior).
   * When true (default), clicking the selected option deselects it.
   */
  allowDeselect?: boolean;
  onValueChange: ({ value }: { value: string | null }) => void;
};

/**
 * Shared layout for all interior-designer option selectors: outlined toggle
 * buttons in a two-column grid with a consistent gap, so groups look the same
 * and never overflow their container.
 */
export function OptionGroup({
  options,
  value,
  disabled,
  allowDeselect = true,
  onValueChange,
}: OptionGroupProps) {
  return (
    <Grid columns={2} gap="xs" paddingY="xs">
      {options.map((option) => {
        const isSelected = value === option.id;
        return (
          <Button
            key={option.id}
            variant="outlined"
            color={isSelected ? "surfacePrimary" : "surfaceNeutralPrimary"}
            size="lg"
            className={optionButtonStyle}
            leading={option.leading}
            disabled={disabled && !isSelected}
            onClick={() => {
              if (option.id === value) {
                if (allowDeselect) onValueChange({ value: null });
                return;
              }
              onValueChange({ value: option.id });
            }}
          >
            {option.label}
          </Button>
        );
      })}
    </Grid>
  );
}
